import { KeyboardArrowDownRounded, KeyboardArrowRightRounded } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useCoaMapping } from "@/hooks/useAccounting";
import type { AccountMappingException, AccountMappingRow } from "@/interfaces/accounting";
import { buildCsv, type CsvColumn, downloadCsv } from "@/lib/csv";

const th = {
  fontSize: 11,
  letterSpacing: "0.6px",
  textTransform: "uppercase" as const,
  color: "var(--color-graphite)",
  fontWeight: 600,
  borderBottom: "1px solid var(--color-card-border)",
};

/** "Zoom" / "Zoom › Sales team" / "All clients, refund only" — who an override actually applies to. */
function scopeOf(e: AccountMappingException): string {
  const who = [e.organizationName, e.groupName].filter(Boolean).join(" › ");
  const base = who || "All clients";
  return e.reasonCode ? `${base} · ${e.reasonCode} only` : base;
}

/**
 * One CSV row per binding, PLUS one per override.
 *
 * Flattened rather than a count in a column: the download is what gets mailed to the controller, and
 * a file that says 23040 with "2 overrides" beside it is unusable — they'd have to come back to the
 * screen for the numbers that actually matter. `Applies to` says "Default" on the binding row and
 * names the client on an override row, so the file sorts and filters on its own.
 */
type CsvRow = {
  account: AccountMappingRow;
  exception: AccountMappingException | null;
};

const CSV_COLUMNS: CsvColumn<CsvRow>[] = [
  { label: "Sub Ledger Key", value: (r) => r.account.accountKey },
  { label: "Applies to", value: (r) => (r.exception ? scopeOf(r.exception) : "Default") },
  {
    label: "COA Account No",
    value: (r) => r.exception?.qbAccountNumber ?? r.account.qbAccountNumber ?? "",
  },
  {
    label: "COA Account Name",
    value: (r) => r.exception?.qbAccountName ?? r.account.qbAccountName ?? "",
  },
  { label: "Description", value: (r) => r.account.description ?? "" },
  { label: "Type", value: (r) => r.account.accountType },
  {
    label: "Status",
    value: (r) => {
      if (r.exception) return r.exception.resolves ? "Override" : "Override — doesn't resolve";
      if (!r.account.isActive) return "Unassigned";
      return r.account.resolves ? "OK" : "Doesn't resolve";
    },
  },
  {
    label: "Effective",
    value: (r) =>
      r.exception ? `${r.exception.effectiveFrom} to ${r.exception.effectiveTill ?? "open"}` : "",
  },
  { label: "Note", value: (r) => r.exception?.note ?? "" },
];

/** The exceptions table shown when a row is expanded. */
function ExceptionDetail({ exceptions }: { exceptions: AccountMappingException[] }) {
  return (
    <Box
      sx={{
        px: 2,
        py: 1.5,
        bgcolor: "var(--color-off-white, #fafafa)",
        borderTop: "1px solid var(--color-card-border)",
      }}
    >
      <Typography sx={{ fontSize: 12, color: "var(--color-graphite)", mb: 1 }}>
        These clients post this key somewhere other than the default above.
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={th}>Applies to</TableCell>
            <TableCell sx={th}>Posts to</TableCell>
            <TableCell sx={th}>Effective</TableCell>
            <TableCell sx={th}>Note</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {exceptions.map((e) => (
            <TableRow key={`${e.organizationName ?? ""}-${e.groupName ?? ""}-${e.qbAccountNumber}`}>
              <TableCell sx={{ fontSize: 12.5 }}>{scopeOf(e)}</TableCell>
              <TableCell sx={{ fontSize: 12.5 }}>
                <span style={{ fontFamily: "monospace" }}>{e.qbAccountNumber}</span>
                {e.qbAccountName ? (
                  <Typography sx={{ fontSize: 12, color: "var(--color-graphite)" }}>
                    {e.qbAccountName}
                  </Typography>
                ) : (
                  <Typography sx={{ fontSize: 12, color: "var(--color-red, #c00)" }}>
                    Not found in COA
                  </Typography>
                )}
              </TableCell>
              <TableCell sx={{ fontSize: 12.5, whiteSpace: "nowrap" }}>
                {e.effectiveFrom} → {e.effectiveTill ?? "open"}
              </TableCell>
              <TableCell sx={{ fontSize: 12, color: "var(--color-graphite)" }}>
                {e.note ?? "—"}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

/**
 * The Chart of Account Mapper — where every subledger key posts in QuickBooks.
 *
 * Read-only. The bindings are ours, not the client's, and they change by migration or seed; this is
 * the screen a financial controller reads to check them, not a place to edit them. Every column,
 * description included, comes from gl_accounts, so a binding added later renders complete.
 *
 * Two things it deliberately surfaces that a plain mapping would not:
 *
 *  - Keys with NO account assigned yet (inactive). They are excluded from the posting picker, so
 *    without this screen the only trace of them is a line in a design document.
 *  - Keys with per-client exceptions, which EXPAND rather than sitting in the status column. An
 *    override is not a health state — the binding is fine, it just isn't the whole truth — and
 *    listing it beside OK/error invites reading it as a fault.
 */
export default function CoaMappingPanel() {
  const { data, isLoading } = useCoaMapping();
  const [expanded, setExpanded] = useState<string | null>(null);

  if (isLoading) return <Box sx={{ p: 2 }}>Loading…</Box>;

  const accounts = data?.accounts ?? [];
  const broken = accounts.filter((a) => a.isActive && !a.resolves);
  const unassigned = accounts.filter((a) => !a.isActive);

  const onExport = () => {
    const rows: CsvRow[] = accounts.flatMap((account) => [
      { account, exception: null },
      ...account.exceptions.map((exception) => ({ account, exception })),
    ]);
    downloadCsv("coa-mapping.csv", buildCsv(CSV_COLUMNS, rows));
  };

  return (
    <Box sx={{ fontFamily: "var(--font-lato)" }}>
      <Box
        sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}
      >
        <Typography sx={{ fontSize: 13, color: "var(--color-graphite)", mb: 2, maxWidth: 780 }}>
          Every subledger key and the QuickBooks account it posts to. Names resolve against{" "}
          <strong>{data?.coaVersionLabel ?? "no COA version"}</strong>
          {data?.coaVersionId != null && ` (version ${data.coaVersionId})`}. QuickBooks matches on
          account NAME on import, so a rename there must be re-imported here.
        </Typography>
        <Button
          variant="outlined"
          size="small"
          onClick={onExport}
          disabled={accounts.length === 0}
          sx={{
            flexShrink: 0,
            textTransform: "none",
            borderRadius: "var(--radius-btn)",
            borderColor: "var(--color-card-border)",
            color: "var(--color-rodeo-black)",
            whiteSpace: "nowrap",
          }}
        >
          Export CSV
        </Button>
      </Box>

      {broken.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>
            {broken.length} {broken.length === 1 ? "binding does" : "bindings do"} not resolve
          </Typography>
          <Typography sx={{ fontSize: 12.5 }}>
            The bound account number isn't in the current COA. Postings that use{" "}
            {broken.length === 1 ? "it" : "them"} are held in Needs Review instead of failing, so
            they are excluded from every export until the mapping is corrected.
          </Typography>
        </Alert>
      )}

      {unassigned.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2.5 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>
            {unassigned.length} {unassigned.length === 1 ? "key has" : "keys have"} no account
            assigned
          </Typography>
          <Typography sx={{ fontSize: 12.5 }}>
            Open items for the accountant — nothing can post to{" "}
            {unassigned.length === 1 ? "it" : "them"} until an account is chosen.
          </Typography>
        </Alert>
      )}

      <Box
        sx={{
          border: "1px solid var(--color-card-border)",
          borderRadius: "var(--radius-card)",
          overflowX: "auto",
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...th, width: 44 }} />
              <TableCell sx={th}>Sub Ledger Key</TableCell>
              <TableCell sx={th}>COA Account No</TableCell>
              <TableCell sx={th}>COA Account Name</TableCell>
              <TableCell sx={th}>Description</TableCell>
              <TableCell sx={th}>Type</TableCell>
              <TableCell sx={{ ...th, textAlign: "right" }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {accounts.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} sx={{ color: "var(--color-graphite)", py: 3 }}>
                  No account bindings configured.
                </TableCell>
              </TableRow>
            )}
            {accounts.map((a) => {
              const hasExceptions = a.exceptions.length > 0;
              const isOpen = expanded === a.accountKey;
              return [
                <TableRow key={a.accountKey} hover>
                  <TableCell sx={{ width: 44 }}>
                    {hasExceptions && (
                      <IconButton
                        size="small"
                        aria-label={isOpen ? "Hide overrides" : "Show overrides"}
                        onClick={() => setExpanded(isOpen ? null : a.accountKey)}
                      >
                        {isOpen ? (
                          <KeyboardArrowDownRounded sx={{ fontSize: 18 }} />
                        ) : (
                          <KeyboardArrowRightRounded sx={{ fontSize: 18 }} />
                        )}
                      </IconButton>
                    )}
                  </TableCell>
                  <TableCell sx={{ fontFamily: "monospace", fontSize: 12.5 }}>
                    {a.accountKey}
                  </TableCell>
                  <TableCell sx={{ fontFamily: "monospace", fontSize: 12.5 }}>
                    {a.qbAccountNumber ?? "—"}
                  </TableCell>
                  <TableCell sx={{ fontSize: 13 }}>
                    {a.qbAccountName ?? (
                      <Typography
                        component="span"
                        sx={{ fontSize: 13, color: "var(--color-red, #c00)" }}
                      >
                        {a.qbAccountNumber ? "Not found in COA" : "—"}
                      </Typography>
                    )}
                    {hasExceptions && (
                      <Typography sx={{ fontSize: 11.5, color: "var(--color-graphite)" }}>
                        {a.exceptions.length} client{" "}
                        {a.exceptions.length === 1 ? "override" : "overrides"} — expand to see
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ fontSize: 12.5, color: "var(--color-graphite)", maxWidth: 380 }}>
                    {a.description ?? "—"}
                  </TableCell>
                  <TableCell sx={{ fontSize: 12.5 }}>{a.accountType}</TableCell>
                  <TableCell sx={{ textAlign: "right", whiteSpace: "nowrap" }}>
                    {!a.isActive ? (
                      <Chip
                        size="small"
                        label="Unassigned"
                        color="warning"
                        variant="outlined"
                        sx={{ height: 20 }}
                      />
                    ) : a.resolves ? (
                      <Typography
                        component="span"
                        sx={{ fontSize: 12.5, color: "var(--color-graphite)" }}
                      >
                        OK
                      </Typography>
                    ) : (
                      <Chip
                        size="small"
                        label="Doesn't resolve"
                        color="error"
                        variant="outlined"
                        sx={{ height: 20 }}
                      />
                    )}
                  </TableCell>
                </TableRow>,
                isOpen ? (
                  <TableRow key={`${a.accountKey}-detail`}>
                    <TableCell colSpan={7} sx={{ p: 0, borderBottom: "none" }}>
                      <ExceptionDetail exceptions={a.exceptions} />
                    </TableCell>
                  </TableRow>
                ) : null,
              ];
            })}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
}
